import ViewerBlock from "@/react/story/components/viewers/ViewerBlock";
import { DEFAULTS } from "@/react/story/data";
import type { Route } from "@/react/story/types";
import { useConfig } from "@/react/story/providers/ConfigContext";
import { useFonts } from "@/react/story/providers/FontsContext";
import { useItems } from "@/react/story/providers/ItemsContext";
import { useStory } from "@/react/story/providers/StoryContext";
import PreloadUtils from "@/react/story/utils/PreloadUtils";
import TemplatingUtils from "@/react/story/utils/TemplatingUtils";
import { type CSSProperties, type MouseEvent, useCallback, useEffect, useRef, useState } from "react";

// Au-delà de ce déplacement (px), le geste a une direction : horizontal, il fait tourner le
// cube ; vertical, il est rendu au navigateur pour faire défiler une page longue.
const SWIPE_LOCK_DISTANCE = 8;
// Part de la largeur à parcourir pour que la page voisine s'installe au relâchement ;
// en dessous, la page courante revient en place.
const SWIPE_COMMIT_RATIO = 1 / 3;
// Un geste vif (px/ms) tourne la page même s'il est court, comme sur Instagram, pourvu
// qu'il ait parcouru au moins `SWIPE_FLICK_DISTANCE` (px).
const SWIPE_COMMIT_VELOCITY = 0.4;
const SWIPE_FLICK_DISTANCE = 30;
// Durée (ms) de la fin de rotation ou du retour en place, alignée sur la feuille de style.
const SWIPE_SETTLE_DURATION = 300;
// Durée (ms) d'une rotation complète déclenchée par un toucher ou une flèche. Plus longue
// que la fin d'un glissement : ici le doigt n'a pas préparé l'œil au mouvement, et un quart
// de tour en 300 ms se lit comme un saut.
const TAP_TURN_DURATION = 600;
// Assombrissement maximal de la face qui s'éloigne, pour lire le relief du cube.
const CUBE_SHADE = 0.45;
// Attente maximale (ms) des images d'une page avant de lancer ses animations malgré tout.
const ANIMATION_MAX_WAIT = 3000;
// La barre de progression de la page en cours se remplit en `PROGRESS_DURATION` (ms),
// comme dans les stories des réseaux sociaux : elle dit qu'il y a une suite.
const PROGRESS_DURATION = 6000;
// Amorce de rotation : quand la barre est pleine, le cube s'entrouvre de `PEEK_ANGLE` degrés
// sur la page suivante puis revient, une seule fois par page. Chaque trajet dure
// `PEEK_DURATION` (ms, aligné sur la feuille de style). La page ne change jamais seule :
// une story se lit, et une page vue doit rester une page que le lecteur a choisi d'ouvrir.
const PEEK_ANGLE = 22;
const PEEK_DURATION = 600;
// À l'ouverture de la story, la première page joue aussi l'amorce d'emblée, `INTRO_PEEK_DELAY`
// (ms) après son affichage complet : un nouveau lecteur comprend tout de suite qu'il y a
// plusieurs pages, sans attendre la fin de la barre.
const INTRO_PEEK_DELAY = 700;
// Aux bords du parcours il n'y a pas de page à dévoiler : le cube résiste (il ne tourne
// que d'une fraction du geste, `EDGE_MAX_ANGLE` degrés au plus) puis revient. Le geste a
// bien été pris, il n'y a simplement rien de ce côté. Un toucher y déclenche le même
// rebond, de `EDGE_BOUNCE_ANGLE` degrés, en `EDGE_BOUNCE_DURATION` (ms) par trajet.
const EDGE_RESISTANCE = 0.25;
const EDGE_MAX_ANGLE = 10;
const EDGE_BOUNCE_ANGLE = 8;
const EDGE_BOUNCE_DURATION = 180;
// Un toucher dans le premier quart de la largeur ramène à la page précédente, ailleurs il
// passe à la suivante : la convention des stories.
const TAP_PREVIOUS_RATIO = 0.25;
// Le navigateur peut émettre un clic à la fin d'un glissement : il ne compte pas (ms).
const TAP_AFTER_SWIPE_DELAY = 400;
// Ce qui se touche pour autre chose que changer de page.
const TAP_IGNORED = "a, button, video, audio, iframe, select, input, textarea, label";

type Side = -1 | 1;

type Gesture = {
    startX: number;
    startY: number;
    startedAt: number;
    width: number;
    lock: "horizontal" | "vertical" | null;
};

// Rotation en cours : l'angle du cube (négatif vers la page suivante), la page qu'on
// dévoile et de quel côté, et si le cube finit son mouvement seul.
type Turn = {
    angle: number;
    neighbour: Route | null;
    // Côté de la page dévoilée : 1 à droite (la suivante), -1 à gauche (la précédente).
    side: Side;
    settling: boolean;
    target: Route | null;
    // Largeur du cadre mesurée au début du mouvement : elle fixe la profondeur du cube.
    width: number;
    // Amorce de rotation : `out` le cube s'entrouvre, `back` il revient.
    peek: "out" | "back" | null;
    // Vrai pour une rotation complète sans le doigt (toucher, flèche).
    auto?: boolean;
    // Vrai pour un rebond contre un bord du parcours : même aller-retour qu'une amorce,
    // plus court et sans page à montrer.
    bounce?: boolean;
};

const IDLE: Turn = { angle: 0, neighbour: null, side: 1, settling: false, target: null, width: 0, peek: null };

const prefersReducedMotion = (): boolean => typeof window.matchMedia === "function"
    && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export default function Viewer() {
    const Config = useConfig();
    const Story = useStory();
    const Items = useItems();
    const Fonts = useFonts();
    const containerRef = useRef<HTMLDivElement>(null);
    const gesture = useRef<Gesture | null>(null);
    const turnRef = useRef<Turn>(IDLE);
    const settleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastSwipeAt = useRef(0);
    const [ turn, setTurnState ] = useState<Turn>(IDLE);
    // Pages dont la barre de progression est allée au bout : elle reste pleine si on y
    // revient, et l'amorce de rotation ne s'y rejoue pas.
    const [ completedUids, setCompletedUids ] = useState<ReadonlySet<string>>(() => new Set());
    // L'amorce d'ouverture ne joue qu'une fois, et plus du tout si le lecteur a déjà bougé.
    const [ introPlayed, setIntroPlayed ] = useState(false);
    // Pages dont les images sont arrivées. La barre de progression attend que la page
    // courante soit entière ; les pages voisines ne sont construites d'avance qu'ensuite, pour ne pas
    // lui disputer la connexion.
    const [ loadedUids, setLoadedUids ] = useState<ReadonlySet<string>>(() => new Set());
    const markLoaded = useCallback((uid: string) => {
        setLoadedUids(previous => previous.has(uid) ? previous : new Set(previous).add(uid));
    }, []);
    // Vrai quand la story occupe toute la largeur de l'écran (téléphone) : la barre de
    // progression peut alors être épinglée à la fenêtre sans déborder du cadre.
    const [ isFullWidth, setIsFullWidth ] = useState(false);
    // Largeur réelle du cadre : elle fixe la profondeur du cube, au repos comme en
    // mouvement, pour que rien ne change d'échelle quand une rotation démarre.
    const [ frameWidth, setFrameWidth ] = useState(0);
    const storyWidth = Story.story?.config?.width ?? DEFAULTS.STORY.width;
    const storyHeight = Story.story?.config?.height ?? DEFAULTS.STORY.height;

    const setTurn = (next: Turn) => {
        turnRef.current = next;
        setTurnState(next);
    };

    const clearSettleTimer = () => {
        if (settleTimer.current) {
            clearTimeout(settleTimer.current);
            settleTimer.current = null;
        }
    };

    useEffect(() => {
        if (typeof window.matchMedia !== "function") {
            return;
        }
        // Tolérance de quelques pixels : un iPhone 17 Pro fait 402 px pour un cadre de 400.
        const query = window.matchMedia(`(max-width: ${storyWidth + 16}px)`);
        const update = () => setIsFullWidth(query.matches);
        update();
        query.addEventListener("change", update);
        return () => query.removeEventListener("change", update);
    }, [ storyWidth ]);

    useEffect(() => {
        const measure = () => setFrameWidth(containerRef.current?.clientWidth ?? 0);
        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, [ isFullWidth ]);

    const routeId = Items.current?.id;

    // Sur un écran plus bas que la story, `#story-viewer-page` défile : chaque page doit
    // s'ouvrir en haut, pas à la hauteur où le lecteur a quitté la précédente.
    useEffect(() => {
        const page = containerRef.current?.closest("#story-viewer-page");
        if (page) {
            page.scrollTop = 0;
        }
    }, [ routeId ]);

    // Trois pages au plus sont chargées à la fois : celle qu'on lit, la suivante et la
    // précédente. Les voisines se chargent pendant la lecture, pour que la face du cube
    // qui se dévoile au glissement ne soit pas vide, d'un côté comme de l'autre (on peut
    // arriver au milieu du parcours par la barre de progression).
    const nextPage = Items.next?.page;
    const previousPage = Items.previous?.page;
    useEffect(() => {
        PreloadUtils.preloadPage(nextPage);
        PreloadUtils.preloadPage(previousPage);
    }, [ nextPage, previousPage ]);

    const goto = (target: Route | null) => {
        setIntroPlayed(true);
        Items.goto(target);
    };

    // Fin de mouvement : la page voisine devient la page courante, ou la page courante
    // se remet d'aplomb. Dans les deux cas le cube repart de zéro, sans transition.
    // L'amorce, elle, enchaîne d'abord son retour.
    const finishTurn = () => {
        clearSettleTimer();
        const current = turnRef.current;
        if (!current.settling) {
            return;
        }
        if (current.peek === "out") {
            setTurn({ ...current, angle: 0, peek: "back" });
            settleTimer.current = setTimeout(finishTurn, (current.bounce ? EDGE_BOUNCE_DURATION : PEEK_DURATION) + 50);
            return;
        }
        setTurn(IDLE);
        if (current.target) {
            goto(current.target);
        }
    };

    const settle = (target: Route | null) => {
        const current = turnRef.current;
        if (target) {
            setTurn({ ...current, angle: -90 * current.side, settling: true, target });
        } else if (current.angle !== 0) {
            setTurn({ ...current, angle: 0, settling: true, target: null });
        } else {
            setTurn(IDLE);
            return;
        }
        // Filet si `transitionend` ne vient pas (onglet en arrière-plan, animation coupée).
        settleTimer.current = setTimeout(finishTurn, SWIPE_SETTLE_DURATION + 50);
    };

    // Rotation complète sans le doigt : un toucher ou une flèche tournent le cube comme
    // un glissement, pour que les deux gestes donnent le même retour.
    const turnTo = (side: Side) => {
        const target = side === 1 ? Items.next : Items.previous;
        const current = turnRef.current;
        if (current.settling && !current.peek) {
            return;
        }
        clearSettleTimer();
        const width = containerRef.current?.clientWidth || storyWidth;
        if (!target) {
            // Bord du parcours : le cube rebondit au lieu de rester sourd au toucher.
            setTurn({ angle: -EDGE_BOUNCE_ANGLE * side, neighbour: null, side, settling: true, target: null, width, peek: "out", bounce: true });
            settleTimer.current = setTimeout(finishTurn, EDGE_BOUNCE_DURATION + 50);
            return;
        }
        setTurn({
            angle: -90 * side,
            neighbour: target,
            side,
            settling: true,
            target,
            width,
            peek: null,
            auto: true,
        });
        settleTimer.current = setTimeout(finishTurn, TAP_TURN_DURATION + 50);
    };

    // Amorce de rotation : le cube s'entrouvre sur la page suivante puis revient. Rend vrai
    // si elle est partie.
    const peekNext = (): boolean => {
        const next = Items.next;
        const current = turnRef.current;
        if (!next || gesture.current || current.settling || current.angle !== 0 || prefersReducedMotion()) {
            return false;
        }
        setTurn({
            angle: -PEEK_ANGLE,
            neighbour: next,
            side: 1,
            settling: true,
            target: null,
            width: containerRef.current?.clientWidth || storyWidth,
            peek: "out",
        });
        settleTimer.current = setTimeout(finishTurn, PEEK_DURATION + 50);

        return true;
    };

    // Les écouteurs et les minuteries sont posés une fois puis rappelés plus tard : ils
    // passent par cette référence pour toujours appeler la version du jour.
    const live = useRef({ settle, finishTurn, peekNext });
    useEffect(() => {
        live.current = { settle, finishTurn, peekNext };
    });

    // Suivi du doigt en écouteurs natifs : React pose les siens en `passive`, ce qui
    // interdit `preventDefault()`, or il faut retenir le défilement vertical dès qu'un
    // glissement horizontal est engagé.
    useEffect(() => {
        const container = containerRef.current;
        if (!container) {
            return;
        }

        const onStart = (event: globalThis.TouchEvent) => {
            // Le doigt reprend la main sur une amorce en cours.
            if (turnRef.current.peek) {
                clearSettleTimer();
                setTurn(IDLE);
            }
            if (event.touches.length !== 1 || turnRef.current.settling) {
                gesture.current = null;
                return;
            }
            const touch = event.touches[0];
            gesture.current = {
                startX: touch.clientX,
                startY: touch.clientY,
                startedAt: event.timeStamp,
                width: container.clientWidth || storyWidth,
                lock: null,
            };
        };

        // Angle du cube pour un déplacement donné : proportionnel à la largeur, borné à un
        // quart de tour. Sans page de ce côté, le cube résiste et ne tourne que de
        // quelques degrés.
        const angleFor = (dx: number, width: number): { angle: number; neighbour: Route | null; side: Side } => {
            const side: Side = dx < 0 ? 1 : -1;
            const neighbour = side === 1 ? Items.next : Items.previous;
            if (dx === 0) {
                return { angle: 0, neighbour: null, side };
            }
            const full = (dx / width) * 90;
            if (!neighbour) {
                return { angle: Math.max(-EDGE_MAX_ANGLE, Math.min(EDGE_MAX_ANGLE, full * EDGE_RESISTANCE)), neighbour: null, side };
            }

            return { angle: Math.max(-90, Math.min(90, full)), neighbour, side };
        };

        const onMove = (event: globalThis.TouchEvent) => {
            const g = gesture.current;
            if (!g || event.touches.length !== 1) {
                return;
            }
            const touch = event.touches[0];
            const dx = touch.clientX - g.startX;
            const dy = touch.clientY - g.startY;
            if (!g.lock) {
                if (Math.abs(dx) < SWIPE_LOCK_DISTANCE && Math.abs(dy) < SWIPE_LOCK_DISTANCE) {
                    return;
                }
                g.lock = Math.abs(dx) > Math.abs(dy) ? "horizontal" : "vertical";
            }
            if (g.lock !== "horizontal") {
                return;
            }
            if (event.cancelable) {
                event.preventDefault();
            }
            setTurn({ ...angleFor(dx, g.width), settling: false, target: null, width: g.width, peek: null });
        };

        const onEnd = (event: globalThis.TouchEvent) => {
            const g = gesture.current;
            gesture.current = null;
            if (!g) {
                return;
            }
            const touch = event.changedTouches[0];
            const dx = touch ? touch.clientX - g.startX : 0;
            const dy = touch ? touch.clientY - g.startY : 0;
            // Un relâchement sans mouvement intermédiaire (geste très bref) se lit ici.
            const horizontal = g.lock === "horizontal" || (!g.lock && Math.abs(dx) >= SWIPE_LOCK_DISTANCE && Math.abs(dx) > Math.abs(dy));
            if (g.lock || horizontal) {
                lastSwipeAt.current = Date.now();
            }
            if (!horizontal) {
                live.current.settle(null);
                return;
            }
            const { angle, neighbour, side } = angleFor(dx, g.width);
            const elapsed = Math.max(1, event.timeStamp - g.startedAt);
            const flick = Math.abs(dx) >= SWIPE_FLICK_DISTANCE && Math.abs(dx) / elapsed >= SWIPE_COMMIT_VELOCITY;
            const commits = neighbour && (Math.abs(dx) >= g.width * SWIPE_COMMIT_RATIO || flick);
            setTurn({ angle, neighbour, side, settling: false, target: null, width: g.width, peek: null });
            live.current.settle(commits ? neighbour : null);
        };

        const onCancel = () => {
            gesture.current = null;
            live.current.settle(null);
        };

        container.addEventListener("touchstart", onStart, { passive: true });
        container.addEventListener("touchmove", onMove, { passive: false });
        container.addEventListener("touchend", onEnd);
        container.addEventListener("touchcancel", onCancel);
        return () => {
            container.removeEventListener("touchstart", onStart);
            container.removeEventListener("touchmove", onMove);
            container.removeEventListener("touchend", onEnd);
            container.removeEventListener("touchcancel", onCancel);
        };
        // `Items` change à chaque page : les écouteurs doivent toujours voir les voisins du jour.
    }, [ Items, storyWidth ]);

    useEffect(() => clearSettleTimer, []);

    const idle = !turn.settling && turn.angle === 0;

    // Amorce d'ouverture : sur la première page d'un parcours, dès qu'elle est affichée en
    // entier. Un doigt posé à ce moment la reporte ; un changement de page l'annule.
    const firstUid = !Items.previous && Items.next ? Items.current?.page?.uid ?? null : null;
    const canIntro = Config.isOnline && !introPlayed && null !== firstUid && loadedUids.has(firstUid);
    useEffect(() => {
        if (!canIntro || !idle) {
            return;
        }
        const timer = setTimeout(() => {
            if (live.current.peekNext()) {
                setIntroPlayed(true);
            }
        }, INTRO_PEEK_DELAY);
        return () => clearTimeout(timer);
    }, [ canIntro, idle ]);

    const route = Items.current;
    if (!route) {
        return null;
    }

    // Fin de la barre de progression : la page est marquée terminée et, s'il y a une
    // suite, l'amorce de rotation joue.
    const onProgressEnd = () => {
        const uid = route.page.uid;
        setCompletedUids(previous => previous.has(uid) ? previous : new Set(previous).add(uid));
        peekNext();
    };

    // État de la barre de la page courante : pleine d'emblée dans l'éditeur ou sur une page
    // déjà terminée ; sinon elle se remplit dès que la page est affichée en entier, et
    // marque une pause pendant que le cube bouge.
    const currentIndex = Items.routes.findIndex(r => r.id === route.id);
    const progressClass = !Config.isOnline || completedUids.has(route.page.uid)
        ? "full"
        : !loadedUids.has(route.page.uid) ? "" : idle ? "filling" : "filling paused";

    // Toucher la page change de page, que les flèches soient affichées ou non : la plupart
    // des lecteurs touchent l'écran plutôt que de glisser. Un lien, une vidéo, un sélecteur
    // gardent leur propre toucher.
    const onTap = (event: MouseEvent<HTMLDivElement>) => {
        if (!Config.isOnline || Date.now() - lastSwipeAt.current < TAP_AFTER_SWIPE_DELAY) {
            return;
        }
        if ((event.target as HTMLElement).closest(TAP_IGNORED)) {
            return;
        }
        const rect = event.currentTarget.getBoundingClientRect();
        const ratio = rect.width > 0 ? (event.clientX - rect.left) / rect.width : 1;
        turnTo(ratio < TAP_PREVIOUS_RATIO ? -1 : 1);
    };

    // La hauteur de la story est transmise aux faces : quand l'écran est plus bas que
    // la story, le cadre est plafonné à l'écran et chaque face défile pour son compte.
    const frameStyle = Story.getViewerStyle();
    // En pleine largeur, le cadre est plus étroit que la story (390 px pour 400) : sa hauteur
    // suit la largeur réelle pour garder le ratio, sinon une image de fond, qui couvre le
    // cadre, se cale sur la hauteur et perd ses bords. Ce qui dépasse de l'écran se révèle en
    // défilant, le cadre restant calé en haut.
    if (isFullWidth && frameWidth > 0) {
        frameStyle.height = `${Math.floor(frameWidth * storyHeight / storyWidth)}px`;
    }
    const viewerStyle = { ...frameStyle, "--story-height": frameStyle.height } as CSSProperties;
    const half = (turn.width || frameWidth || storyWidth) / 2;
    const progress = Math.abs(turn.angle) / 90;
    const cubeStyle: CSSProperties = {
        transform: `translateZ(${-half}px) rotateY(${turn.angle}deg)`,
    };
    const faceStyle = (side: -1 | 0 | 1): CSSProperties => ({
        transform: `rotateY(${side * 90}deg) translateZ(${half}px)`,
    });

    // Les pages voisines sont construites d'avance et tenues à plat juste derrière la page
    // courante, donc déjà dessinées quand le cube se met à tourner : il ne reste qu'à les
    // faire pivoter. Une face montée au début du geste arrive blanche : Safari sur iPhone
    // ne décode ses grandes images de fond qu'en cours de rotation. Pas dans l'aperçu de
    // l'éditeur, qu'on ne fait pas glisser et que chaque saisie redessine.
    const restingStyle: CSSProperties = { transform: `translateZ(${half - 1}px)` };
    const prewarm = Config.isOnline && loadedUids.has(route.page.uid) && !PreloadUtils.savesData();
    const faceOn = (side: Side): Route | null => {
        if (turn.neighbour && turn.side === side) {
            return turn.neighbour;
        }
        const candidate = side === 1 ? Items.next : Items.previous;

        return prewarm && candidate && !PreloadUtils.hasMedia(candidate.page) ? candidate : null;
    };
    const previousFace = faceOn(-1);
    const nextFace = faceOn(1);
    // Voisines d'une page voisine, pour dessiner ses flèches avant qu'elle soit courante.
    const around = (face: Route): { previous: Route | null; next: Route | null } => {
        const index = Items.routes.findIndex(r => r.id === face.id);

        return {
            previous: index > 0 ? Items.routes[index - 1] : null,
            next: index >= 0 ? Items.routes[index + 1] ?? null : null,
        };
    };
    // Au repos les voisines restent à plat ; dès que le cube bouge, chacune prend sa face.
    const neighbourStyle = (side: Side): CSSProperties => idle ? restingStyle : faceStyle(side);
    const neighbourShade = (side: Side): number => turn.neighbour && turn.side === side ? (1 - progress) * CUBE_SHADE : 0;
    // Face vide montrée par un rebond contre un bord du parcours.
    const endSide: Side | null = !idle && !turn.neighbour ? turn.side : null;
    const endFace = (side: Side) => endSide === side
        ? <div key="end" className="story-viewer story-cube-end" style={faceStyle(side)} aria-hidden="true" />
        : null;
    const cubeClass = turn.peek ? (turn.bounce ? "bouncing" : "peeking") : turn.auto ? "turning" : turn.settling ? "settling" : undefined;

    return (
        <>
            {Fonts.getStyleNode()}
            <div
                ref={containerRef}
                id="story-viewer-container"
                className={isFullWidth ? "full-width" : undefined}
                style={viewerStyle}
            >
                <nav>
                    {Items.routes.map((r, i) => (
                        <button
                            key={i}
                            type="button"
                            className={i === currentIndex ? "active" : i < currentIndex ? "done" : ""}
                            onClick={() => goto(r)}
                        >
                            Page {i + 1}
                            {i === currentIndex ? (
                                <ProgressFill
                                    key={r.id}
                                    state={progressClass}
                                    onEnd={onProgressEnd}
                                />
                            ) : null}
                        </button>
                    ))}
                </nav>
                {route.page ? (
                    <div id="story-cube" className={cubeClass} onClick={onTap}>
                        <div
                            className="story-cube-faces"
                            style={cubeStyle}
                            onTransitionEnd={event => event.target === event.currentTarget && finishTurn()}
                        >
                            {/* Ordre précédente, courante, suivante : en avançant comme en
                                reculant, aucun élément n'a à être déplacé, donc redessiné. */}
                            {previousFace ? (
                                <ViewerPage
                                    key={previousFace.page.uid}
                                    route={previousFace}
                                    {...around(previousFace)}
                                    onLoaded={markLoaded}
                                    style={neighbourStyle(-1)}
                                    shade={neighbourShade(-1)}
                                />
                            ) : endFace(-1)}
                            <ViewerPage
                                key={route.page.uid}
                                route={route}
                                previous={Items.previous}
                                next={Items.next}
                                onTurn={turnTo}
                                onLoaded={markLoaded}
                                still={idle}
                                style={faceStyle(0)}
                                shade={progress * CUBE_SHADE}
                            />
                            {nextFace ? (
                                <ViewerPage
                                    key={nextFace.page.uid}
                                    route={nextFace}
                                    {...around(nextFace)}
                                    onLoaded={markLoaded}
                                    style={neighbourStyle(1)}
                                    shade={neighbourShade(1)}
                                />
                            ) : endFace(1)}
                        </div>
                    </div>
                ) : null}
            </div>
        </>
    );
}

// Chevron des flèches, dessiné en SVG (tracés de Bootstrap Icons) : la story publique n'a
// pas à télécharger une police d'icônes de 134 Ko pour deux flèches.
function Chevron(props: { side: Side }) {
    return (
        <svg className="story-chevron" viewBox="0 0 16 16" width="1em" height="1em" fill="currentColor" aria-hidden="true" focusable="false">
            <path
                fillRule="evenodd"
                d={-1 === props.side
                    ? "M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0"
                    : "M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708"}
            />
        </svg>
    );
}

// La barre qui se remplit sur la page en cours. Sa fin est écoutée par un écouteur natif :
// sous jsdom, React ne relaie pas `animationend`, et ce moment doit rester testable.
function ProgressFill(props: { state: string; onEnd: () => void }) {
    const ref = useRef<HTMLSpanElement>(null);
    const onEnd = useRef(props.onEnd);
    useEffect(() => {
        onEnd.current = props.onEnd;
    });
    useEffect(() => {
        const element = ref.current;
        if (!element) {
            return;
        }
        const listener = () => onEnd.current();
        element.addEventListener("animationend", listener);
        return () => element.removeEventListener("animationend", listener);
    }, []);

    return (
        <span
            ref={ref}
            className={[ "story-progress-fill", props.state ].filter(Boolean).join(" ")}
            style={{ animationDuration: `${PROGRESS_DURATION}ms` }}
        />
    );
}

type PageProps = {
    route: Route;
    style: CSSProperties;
    shade: number;
    // Pages de part et d'autre : elles décident des flèches à dessiner.
    previous?: Route | null;
    next?: Route | null;
    // Absent sur une face voisine : elle n'est qu'un aperçu, ses flèches ne répondent pas.
    onTurn?: (side: Side) => void;
    // Prévenu quand les images de la page sont arrivées.
    onLoaded?: (uid: string) => void;
    // Vrai quand le cube est au repos sur cette page.
    still?: boolean;
};

// Une face du cube : la page avec ses deux blocs, leurs séparateurs et ses flèches. La face
// voisine porte la même clé que la page qu'elle deviendra : quand elle s'installe, c'est le
// même élément qui continue, sans être rejoué.
function ViewerPage(props: PageProps) {
    const { route, onTurn, onLoaded } = props;
    const isCurrent = !!onTurn;
    const page = route.page;
    const faceRef = useRef<HTMLDivElement>(null);
    const [ loaded, setLoaded ] = useState(() => PreloadUtils.pageImages(page).length === 0);

    // Une page construite d'avance ne reçoit ni toucher ni tabulation tant qu'elle n'est
    // pas la page courante. Et une page qu'on quitte remonte en haut, hors de vue : chaque
    // page a son propre défilement et se présente toujours par le haut.
    useEffect(() => {
        const face = faceRef.current;
        if (!face) {
            return;
        }
        face.inert = !isCurrent;
        if (!isCurrent) {
            face.scrollTop = 0;
        }
    }, [ isCurrent ]);

    useEffect(() => {
        let cancelled = false;
        PreloadUtils.whenLoaded(PreloadUtils.pageImages(page), ANIMATION_MAX_WAIT).then(() => {
            if (!cancelled) {
                setLoaded(true);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [ page ]);

    useEffect(() => {
        if (loaded) {
            onLoaded?.(page.uid);
        }
    }, [ loaded, onLoaded, page.uid ]);

    // Les animations des contenus ne partent qu'une fois la page affichée en entier : en
    // place (plus d'aperçu ni de rotation) et ses images arrivées. D'ici là, la feuille
    // de style les tient en pause.
    const ready = isCurrent && !!props.still && loaded;

    // Les flèches sont dessinées sur chaque face, voisines comprises : elles tournent avec
    // la page au lieu d'apparaître une fois la rotation finie. Seules celles de la page
    // courante répondent ; c'est le même bouton qui s'active quand la face s'installe.
    const arrow = (side: Side) => {
        const target = side === -1 ? props.previous : props.next;
        if (!page.arrows || !target) {
            return null;
        }
        const name = side === -1 ? "previous" : "next";

        return (
            <button
                type="button"
                id={isCurrent ? `story-${name}` : undefined}
                className={`story-arrow story-arrow-${name}`}
                aria-label={isCurrent ? (side === -1 ? "Page précédente" : "Page suivante") : undefined}
                tabIndex={isCurrent ? undefined : -1}
                style={{
                    ...TemplatingUtils.style(page.arrows.config, [ "alignItems" ]),
                }}
                onClick={isCurrent ? () => onTurn?.(side) : undefined}
            >
                <strong style={{
                    ...TemplatingUtils.style(page.arrows.config, [ "color", "backgroundColor" ]),
                }}><Chevron side={side} /></strong>
            </button>
        );
    };

    const hasLink = () => {
        return (
            page.top?.contents.some(content => !!content.config.href)
            || page.bottom?.contents.some(content => !!content.config.href)
        );
    };

    return (
        <div
            ref={faceRef}
            id={isCurrent ? "story-viewer" : undefined}
            className={[ "story-viewer", hasLink() ? "has-link" : "", ready ? "ready" : "" ].filter(Boolean).join(" ")}
            style={props.style}
            aria-hidden={isCurrent ? undefined : true}
        >
            <div className="story-viewer-content">
                <div className="d-flex flex-nowrap">
                    <ViewerBlock block={page.top} />
                    {page.top.separator ? (
                        <div
                            style={{
                                flexBasis: TemplatingUtils.percent(DEFAULTS.flexBasis),
                                ...TemplatingUtils.style(page.top.separator?.config, [ "backgroundImage" ]),
                            }}
                        ></div>
                    ) : null}
                </div>
                {page.bottom ? (
                    <div className="d-flex flex-nowrap">
                        {page.bottom.separator ? (
                            <div
                                style={{
                                    flexBasis: TemplatingUtils.percent(DEFAULTS.flexBasis),
                                    ...TemplatingUtils.style(page.bottom.separator?.config),
                                }}
                            ></div>
                        ) : null}
                        <ViewerBlock block={page.bottom} />
                    </div>
                ) : null}
                {arrow(-1)}
                {arrow(1)}
                {/* Toujours présent : l'ajouter au premier mouvement ferait redessiner la face. */}
                <div className="story-cube-shade" style={{ opacity: props.shade }} aria-hidden="true" />
            </div>
        </div>
    );
}
